import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RelatedBooks = ({ books = [], title = "Libros Relacionados" }) => {
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef?.current) {
      const scrollAmount = 280; // Width of one card plus gap
      const currentScroll = scrollContainerRef?.current?.scrollLeft;
      const targetScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef?.current?.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(price);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={12} className="text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={12} className="text-yellow-400 fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-gray-300" />
      );
    }

    return stars;
  };

  if (!books || books?.length === 0) {
    return null;
  }

  return (
    <section className="py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground">
          {title}
        </h2>
        
        {/* Navigation Buttons - Desktop */}
        <div className="hidden md:flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('left')}
            className="h-10 w-10"
          >
            <Icon name="ChevronLeft" size={20} />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('right')}
            className="h-10 w-10"
          >
            <Icon name="ChevronRight" size={20} />
          </Button>
        </div>
      </div>
      {/* Books Carousel */}
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="flex space-x-4 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {books?.map((book) => (
            <Link
              key={book?.id}
              to={`/book-detail-screen?id=${book?.id}`}
              className="flex-shrink-0 w-64 bg-card border border-border rounded-lg overflow-hidden hover:shadow-modal transition-all duration-300 hover-lift group"
            >
              {/* Book Cover */}
              <div className="relative aspect-[3/4] overflow-hidden">
                <Image
                  src={book?.coverImage}
                  alt={book?.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                

                {/* Quick Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-black/50 text-white hover:bg-black/70"
                    onClick={(e) => {
                      e?.preventDefault();
                      // Handle favorite toggle
                    }}
                  >
                    <Icon name="Heart" size={16} />
                  </Button>
                </div>
              </div>

              {/* Book Info */}
              <div className="p-4 space-y-2">
                <h3 className="font-semibold text-foreground line-clamp-2 group-hover:text-accent transition-smooth">
                  {book?.title}
                </h3>
                
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {book?.author}
                </p>

                {/* Rating */}
                <div className="flex items-center space-x-1">
                  <div className="flex items-center">
                    {renderStars(book?.rating)}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    ({book?.reviewCount})
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-accent">
                    {formatPrice(book?.originalPrice || book?.price)}
                  </span>
                </div>

                {/* Stock Status */}
                <div className="flex items-center space-x-1">
                  <Icon 
                    name={book?.inStock ? "CheckCircle" : "XCircle"} 
                    size={14} 
                    className={book?.inStock ? "text-success" : "text-destructive"} 
                  />
                  <span className={`text-xs ${book?.inStock ? "text-success" : "text-destructive"}`}>
                    {book?.inStock ? "En stock" : "Agotado"}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile Navigation Indicators */}
        <div className="md:hidden flex justify-center mt-4 space-x-2">
          {Array.from({ length: Math.ceil(books?.length / 2) })?.map((_, index) => (
            <div
              key={index}
              className="w-2 h-2 rounded-full bg-muted"
            />
          ))}
        </div>
      </div>
      {/* View All Link */}
      <div className="text-center mt-6">
        <Link
          to="/book-catalog-browse-screen"
          className="inline-flex items-center space-x-2 text-accent hover:text-accent/80 font-medium transition-smooth"
        >
          <span>Ver todos los libros</span>
          <Icon name="ArrowRight" size={16} />
        </Link>
      </div>
    </section>
  );
};

export default RelatedBooks;
